import { BlankSlateInline } from "@msuite/ui/blank-slate";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { scaleToTotal } from "@msuite/utils/incoming-invoices";
import type { IncomingInvoiceReviewFormSchema } from "@msuite/utils/schemas";
import { useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import {
	IncomingInvoiceCostAllocationForm,
	shareOf,
} from "@/forms/incoming-invoice-cost-allocation-form";

/** Props Interface */
interface ReviewCostAllocationProps {
	/** The net amount the allocation adds up to — the reduced one under a Kürzung. */
	netAmount: number;
	currency: string;
	/** Whether the organisation keeps any active cost centre to allocate to. */
	hasCostCenters: boolean;
}

/**
 * The cost allocation the approver accepts. A Kürzung moves the net amount it adds up to, and the
 * rows follow it at the shares they had, so the split survives the new total.
 */
export function ReviewCostAllocation({
	netAmount,
	currency,
	hasCostCenters,
}: ReviewCostAllocationProps) {
	/** Context */
	const methods = useFormContext<IncomingInvoiceReviewFormSchema>();

	/** Hooks */
	const { t } = useTranslation();

	/** State */
	// The total the rows were last scaled to; the rows load already adding up to the first one.
	const previousNetAmount = useRef(netAmount);

	/** Effects */
	useEffect(() => {
		if (previousNetAmount.current === netAmount) return;
		previousNetAmount.current = netAmount;
		const allocations = methods.getValues("allocations");
		const amounts = scaleToTotal(
			allocations.map((allocation) => allocation.amount ?? 0),
			netAmount,
		);
		// Row by row, so the field array keeps its rows rather than mounting new ones.
		for (let index = 0; index < amounts.length; index++) {
			const amount = amounts[index] ?? 0;
			methods.setValue(`allocations.${index}.amount`, amount, { shouldDirty: true });
			methods.setValue(`allocations.${index}.share`, shareOf(amount, netAmount));
		}
	}, [netAmount, methods]);

	/** Guard */
	if (!hasCostCenters) {
		return (
			<BlankSlateInline
				label={t("incoming_invoices.decision.no_cost_centers")}
				Icon={icon("Split")}
			>
				{t("incoming_invoices.decision.cost_allocation")}
			</BlankSlateInline>
		);
	}

	/** Render */
	return (
		<div className="v-stack">
			<p className="label">{t("incoming_invoices.decision.cost_allocation")}</p>
			<IncomingInvoiceCostAllocationForm
				netAmount={netAmount}
				currency={currency}
			/>
		</div>
	);
}
